// biome-ignore-all lint/a11y: TODO - address a11y issues later
// biome-ignore-all lint/suspicious/noArrayIndexKey: TODO - address later
import { DndContext } from "@dnd-kit/core";
import { InfoCircledIcon } from "@radix-ui/react-icons";
import {
  type ColumnDef,
  type ExpandedState,
  flexRender,
  getCoreRowModel,
  getExpandedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import {
  ChevronDown,
  Download,
  Expand,
  Filter,
  ListChevronsUpDown,
  ListCollapse,
  Loader2,
  Search,
  Shrink,
  UnfoldHorizontal,
  X,
} from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { ActiveFiltersDisplay } from "@/components/active-filters-display";
import { ChannelsColumnSelector } from "@/components/channels-column-selector";
import { ColumnAliasModal } from "@/components/column-alias-modal";
import { DraggableTableHeader } from "@/components/draggable-table-header";
import { EntityFilterModal } from "@/components/entity-filter-modal";
import { ExportFilenameDialog } from "@/components/export-filename-dialog";
import ResetTableMenu from "@/components/reset-table-menu";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { InfoTooltip } from "@/components/ui/info-tooltip";
import { Input } from "@/components/ui/input";
import { useSidebar } from "@/components/ui/sidebar";
import { Skeleton } from "@/components/ui/skeleton";
import { SortIcon } from "@/components/ui/sort-icon";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import type { ChannelMetrics } from "@/hooks/api/use-channel-metrics";
import {
  type CustomChannel,
  type CustomChannelMetrics,
  type CustomChannelsQueryParams,
  useCustomChannelMetrics as useCustomChannelMetricsAPI,
  useCustomChannelsExport,
  useCustomChannelsWithMetrics,
} from "@/hooks/api/use-custom-channels";
import { useOrganizationSessionStorage } from "@/hooks/use-browser-storage";
import { useChannelFilterState } from "@/hooks/use-channel-filter-state";
import { usePaginatedCSVExport } from "@/hooks/use-csv-export";
import {
  useCustomChannelListFilters,
  useCustomChannelMetrics,
} from "@/hooks/use-custom-channel-list-filters";
import { useMetricCardFilters } from "@/hooks/use-metric-card-filters";
import { usePersistedColumnState } from "@/hooks/use-persisted-column-state";
import { useSearchState } from "@/hooks/use-search-state";
import { useSelectedRulesetId } from "@/hooks/use-selected-ruleset";
import { useTrackedSessionStorage } from "@/hooks/use-tracked-browser-storage";
import {
  constrainedClosestCenter,
  restrictToTableBounds,
} from "@/lib/collision-detection";
import type { ColumnAliases } from "@/lib/column-aliases";
import {
  createBulkColumnResizeHandler,
  createColumnResizeHandler,
  createSingleColumnResizeHandler,
} from "@/lib/column-resize-utils";
import { formatDateForFilename } from "@/lib/date-formatting";
import { cn } from "@/lib/utils";
import { MetricDrilldown } from "@/pages/custom-channels/custom-channels-explorer/metric-drilldown";
import type { SortDirection, UnifiedTableColumn } from "@/types/table-column";
import {
  getCustomChannelColumnDefs,
  getCustomChannelColumns,
} from "./custom-channels-columns";
import {
  DEFAULT_METRIC_ORDER,
  DRILLDOWN_LIMIT,
  HIDDEN_METRICS,
  METRIC_CONFIG,
} from "./custom-channels-metrics-config";

export function CustomChannelsDataTable() {
  const CUSTOM_CHANNEL_METRIC_LIMIT = 5000;

  // Use the sidebar hook to get state
  const { state, isMobile } = useSidebar();

  // Get the selected ruleset id for custom channel classification
  const selectedRulesetId = useSelectedRulesetId();

  // State for sub-channel toggle
  const [includeSubchannel, setIncludeSubchannel] =
    useTrackedSessionStorage<boolean>(
      "custom-channels::list::include-subchannel",
      false,
    );

  // State for grouping mode (true = grouped by channel, false = ungrouped with separate column)
  const [isGrouped, setIsGrouped] = useTrackedSessionStorage<boolean>(
    "custom-channels::list::is-grouped",
    true,
  );

  // State for row expansion - tracked in session storage for saved views
  const [expanded, setExpanded] = useTrackedSessionStorage<ExpandedState>(
    "custom-channels::list::expanded",
    {},
  );

  // Feature flag for sticky parent rows (advanced feature)
  const [enableStickyParents] = useState(true);

  // State for sorting and searching
  const [sortConfig, setSortConfig] = useState<{
    key: string;
    direction: SortDirection;
  }>({
    key: "custom_channel",
    direction: "asc",
  });

  // Use sessionStorage for search state (tab-specific)
  const {
    searchInputValue,
    debouncedSearchQuery,
    handleSearchChange,
    clearSearch,
  } = useSearchState("custom-channels::list::search");

  const [showFilterModal, setShowFilterModal] = useState(false);

  // State for metric drilldown dialog
  const [drilldownState, setDrilldownState] = useState<{
    metric: keyof CustomChannelMetrics;
    channel: string;
    subchannel?: string;
    metricValue?: number | string;
  } | null>(null);

  // Structured filter groups (account / opportunity / touchpoint)
  const channelFilterState = useChannelFilterState("custom-channels::list");
  const {
    accountFilters,
    opportunityFilters,
    touchpointFilters,
    campaignFilters,
    hasAccountFilters,
    hasOpportunityFilters,
    hasTouchpointFilters,
    hasCampaignFilters,
    clearAllFilters: clearAllMetricFilters,
  } = channelFilterState;

  // Total count of active metric filters
  const totalMetricFilterCount = useMemo(
    () =>
      accountFilters.filters.length +
      opportunityFilters.filters.length +
      touchpointFilters.filters.length +
      campaignFilters.filters.length,
    [accountFilters, opportunityFilters, touchpointFilters, campaignFilters],
  );

  // Process metric filters for display
  const {
    displayFilters: metricDisplayFilters,
    filterColumns: metricFilterColumns,
    handleClearFilter: handleClearMetricFilter,
  } = useMetricCardFilters(channelFilterState);

  // Use custom channel list filters hook
  const {
    filters,
    updateFilter,
    clearFilter,
    clearAllFilters,
    hasFilters,
    getDefaultSortSettings,
    getQueryParams,
  } = useCustomChannelListFilters();

  // Use the custom channel metrics configuration hook
  const { selectedMetrics, setSelectedMetrics, getDefaultMetrics } =
    useCustomChannelMetrics();

  // Fetch metrics for the drilldown dialog when a channel is selected
  const metricsToFetch = useMemo(() => {
    return [
      ...new Set([...DEFAULT_METRIC_ORDER, ...HIDDEN_METRICS] as Array<
        keyof CustomChannelMetrics
      >),
    ];
  }, []);

  const {
    data: drilldownChannelMetrics,
    isLoading: isLoadingDrilldownMetrics,
  } = useCustomChannelMetricsAPI({
    channel: drilldownState?.channel || "",
    subchannel: drilldownState?.subchannel,
    metrics: metricsToFetch,
    filters: {
      ...(hasAccountFilters && { account_filters: accountFilters }),
      ...(hasOpportunityFilters && { opportunity_filters: opportunityFilters }),
      ...(hasTouchpointFilters && { touchpoint_filters: touchpointFilters }),
    },
    rule_set_id: selectedRulesetId ?? undefined,
  });

  // Create loading states for the drilldown
  const drilldownLoadingStates = useMemo(() => {
    const states: Record<string, { isLoading: boolean; isError: boolean }> = {};
    metricsToFetch.forEach((metric) => {
      states[metric] = { isLoading: isLoadingDrilldownMetrics, isError: false };
    });
    return states;
  }, [metricsToFetch, isLoadingDrilldownMetrics]);

  const [columnAliases, setColumnAliases] =
    useOrganizationSessionStorage<ColumnAliases>(
      "custom-channels::list::columns::aliases",
      {},
      {
        migration: {
          from: {
            key: "custom-channels::list::columns::aliases",
            storage: "localStorage",
            organizationScoped: true,
          },
          cleanup: true,
        },
      },
    );
  const [showAliasModal, setShowAliasModal] = useState(false);
  const [aliasColumnId, setAliasColumnId] = useState<string>("");

  // State to track if performance message should show
  const [showPerformanceMessage, setShowPerformanceMessage] = useState(false);

  // Reset sort to defaults
  const resetSortToDefaults = useCallback(() => {
    const defaults = getDefaultSortSettings();
    setSortConfig({
      key: defaults.sortColumn,
      direction: defaults.sortDirection,
    });
  }, [getDefaultSortSettings]);

  const handleSelectMetrics = useCallback(
    (metrics: Array<keyof CustomChannelMetrics>) => {
      // Check if any removed metrics are currently used for sorting
      const removedMetrics = selectedMetrics.filter(
        (metric) => !metrics.includes(metric),
      );

      // Check if the current sort is on a metric that's being removed
      if (sortConfig.key.startsWith("metric_")) {
        const currentMetricKey = sortConfig.key.replace(
          "metric_",
          "",
        ) as keyof CustomChannelMetrics;

        if (removedMetrics.includes(currentMetricKey)) {
          const metricConfig = METRIC_CONFIG[currentMetricKey];
          const metricTitle = metricConfig?.title || currentMetricKey;

          toast.warning("Table sort reset", {
            description: `Sort on ${metricTitle} has been removed because the column was deselected.`,
            duration: 8000,
          });

          resetSortToDefaults();
          clearFilter("sortColumn");
          clearFilter("sortDirection");
        }
      }

      setSelectedMetrics(metrics);
    },
    [
      selectedMetrics,
      sortConfig.key,
      setSelectedMetrics,
      resetSortToDefaults,
      clearFilter,
    ],
  );

  // Handle metric click to open drill-down
  const handleMetricClick = useCallback(
    (
      channelName: string,
      metric: keyof CustomChannelMetrics,
      subchannelName?: string,
      metricValue?: number | string,
    ) => {
      setDrilldownState({
        metric,
        channel: channelName,
        subchannel: subchannelName,
        metricValue,
      });
    },
    [],
  );

  // Define columns for the table, including metric columns
  const columns = useMemo<UnifiedTableColumn<CustomChannel>[]>(
    () =>
      getCustomChannelColumns(selectedMetrics, handleMetricClick, isGrouped),
    [selectedMetrics, handleMetricClick, isGrouped],
  );

  // Column visibility state
  const [columnVisibility, setColumnVisibility] = useOrganizationSessionStorage<
    Record<string, boolean>
  >(
    "custom-channels::list::column-visibility",
    columns.reduce(
      (acc, col) => {
        acc[col.key.toString()] = true;
        return acc;
      },
      {} as Record<string, boolean>,
    ),
  );

  // Reset column visibility and metric selection to defaults
  const resetColumnVisibility = useCallback(() => {
    const defaultMetrics = getDefaultMetrics();
    setSelectedMetrics(defaultMetrics);

    const defaultColumns = getCustomChannelColumns(
      defaultMetrics,
      handleMetricClick,
      isGrouped,
    );

    const defaultVisibility = defaultColumns.reduce(
      (acc, col) => {
        acc[col.key.toString()] = true;
        return acc;
      },
      {} as Record<string, boolean>,
    );

    setColumnVisibility(defaultVisibility);
  }, [
    getDefaultMetrics,
    setSelectedMetrics,
    handleMetricClick,
    setColumnVisibility,
    isGrouped,
  ]);

  // Handle clear all filters and reset sort
  const handleClearAllFilters = useCallback(() => {
    const currentSortKey = sortConfig.key;
    if (currentSortKey.startsWith("metric_")) {
      metricSortClearedRef.current = currentSortKey;
    }

    clearAllFilters();
    clearSearch();

    const defaults = getDefaultSortSettings();
    setSortConfig({
      key: defaults.sortColumn,
      direction: defaults.sortDirection as SortDirection,
    });

    updateFilter("sortColumn", defaults.sortColumn);
    updateFilter("sortDirection", defaults.sortDirection);
  }, [
    clearAllFilters,
    getDefaultSortSettings,
    updateFilter,
    sortConfig.key,
    clearSearch,
  ]);

  // Track metric sort cleared
  const metricSortClearedRef = useRef<string | null>(null);

  // When component mounts, set sort from filters
  useEffect(() => {
    if (filters.sortColumn) {
      setSortConfig({
        key: filters.sortColumn as string,
        direction: filters.sortDirection as string as SortDirection,
      });
    } else {
      const defaults = getDefaultSortSettings();
      setSortConfig({
        key: defaults.sortColumn,
        direction: defaults.sortDirection as SortDirection,
      });

      updateFilter("sortColumn", defaults.sortColumn);
      updateFilter("sortDirection", defaults.sortDirection);
    }
  }, [
    filters.sortColumn,
    filters.sortDirection,
    getDefaultSortSettings,
    updateFilter,
  ]);

  // Build query parameters for API calls
  const baseQueryParams = useMemo(() => {
    const metricsFilters = {
      ...(hasAccountFilters && { account_filters: accountFilters }),
      ...(hasOpportunityFilters && { opportunity_filters: opportunityFilters }),
      ...(hasTouchpointFilters && { touchpoint_filters: touchpointFilters }),
      ...(hasCampaignFilters && { campaign_filters: campaignFilters }),
    };

    return {
      ...getQueryParams({
        searchTerm: debouncedSearchQuery,
        columns: columns,
        limit: 100,
        metrics: selectedMetrics.length > 0 ? selectedMetrics : undefined,
        metric_filters:
          Object.keys(metricsFilters).length > 0 ? metricsFilters : undefined,
      }),
      rule_set_id: selectedRulesetId ?? undefined,
    } as CustomChannelsQueryParams;
  }, [
    debouncedSearchQuery,
    selectedMetrics,
    hasAccountFilters,
    accountFilters,
    hasOpportunityFilters,
    opportunityFilters,
    hasTouchpointFilters,
    touchpointFilters,
    hasCampaignFilters,
    campaignFilters,
    getQueryParams,
    columns,
    selectedRulesetId,
  ]);

  // Parent channel metrics
  const parentQueryParams = useMemo<CustomChannelsQueryParams>(() => {
    return {
      ...baseQueryParams,
    };
  }, [baseQueryParams]);

  // Subchannel metrics
  const subchannelQueryParams = useMemo<CustomChannelsQueryParams>(() => {
    return {
      ...baseQueryParams,
      extra_fields: "custom_subchannel",
    };
  }, [baseQueryParams]);

  // Fetch parent channel metrics
  const {
    data: parentChannelsData,
    isLoading: isLoadingParents,
    isError: isParentError,
    error: parentError,
  } = useCustomChannelsWithMetrics(parentQueryParams);

  const { data: subchannelData, isLoading: isLoadingSubchannels } =
    useCustomChannelsWithMetrics(subchannelQueryParams);

  // Determine overall loading and error states
  const isLoadingCustomChannels = isLoadingParents || isLoadingSubchannels;
  const isError = isParentError;
  const error = parentError;

  // Merge parent and subchannel data
  const customChannelsData = useMemo(() => {
    if (!parentChannelsData || !subchannelData) return undefined;

    if (!isGrouped) {
      // In ungrouped mode, use subchannel data (which has the subchannel column)
      return subchannelData;
    }

    return {
      results: [...parentChannelsData.results, ...subchannelData.results],
      total: parentChannelsData.total,
    };
  }, [parentChannelsData, subchannelData, isGrouped]);

  // Custom channels with metrics - transform into nested structure when grouped mode is on
  const customChannels = useMemo(() => {
    const channelsWithMetrics = customChannelsData?.results || [];

    // If grouped mode is on, group by parent channel (regardless of includeSubchannel)
    if (isGrouped) {
      // Group channels by parent channel name
      const groupedChannels = channelsWithMetrics.reduce(
        (acc, channel) => {
          const parentKey = channel.custom_channel;
          if (!acc[parentKey]) {
            acc[parentKey] = [];
          }
          acc[parentKey].push(channel);
          return acc;
        },
        {} as Record<string, CustomChannel[]>,
      );

      // Transform into nested structure
      const nestedChannels: (CustomChannel & { subRows?: CustomChannel[] })[] =
        [];

      Object.entries(groupedChannels).forEach(([channelName, channels]) => {
        const parentRow = channels.find((c) => !c.custom_subchannel);
        const subchannelRows = channels.filter((c) => c.custom_subchannel);

        if (parentRow) {
          if (subchannelRows.length > 0) {
            nestedChannels.push({
              ...parentRow,
              subRows: subchannelRows,
            });
          } else {
            nestedChannels.push(parentRow);
          }
        } else if (subchannelRows.length > 0) {
          // Only warn if there's no active search - when searching, it's expected that
          // subchannels may match without their parent channel matching
          if (!debouncedSearchQuery) {
            console.warn(
              `Parent channel "${channelName}" not found in API response. This indicates an API data issue.`,
            );
          }
          nestedChannels.push({
            custom_channel: channelName,
            metrics: {},
            subRows: subchannelRows,
          });
        }
      });

      // Apply sorting to parent rows and their sub-channels
      const sortedChannels = [...nestedChannels];

      if (sortConfig.key === "custom_channel") {
        // Sort parent channels
        sortedChannels.sort((a, b) => {
          const comparison = a.custom_channel.localeCompare(b.custom_channel);
          return sortConfig.direction === "asc" ? comparison : -comparison;
        });

        // Also sort sub-channels within each parent by subchannel name
        sortedChannels.forEach((parent) => {
          if (parent.subRows && parent.subRows.length > 0) {
            parent.subRows.sort((a, b) => {
              const aName = a.custom_subchannel || "";
              const bName = b.custom_subchannel || "";
              const comparison = aName.localeCompare(bName);
              return sortConfig.direction === "asc" ? comparison : -comparison;
            });
          }
        });
      } else if (sortConfig.key.startsWith("metric_")) {
        const metricKey = sortConfig.key.replace(
          "metric_",
          "",
        ) as keyof CustomChannelMetrics;

        // Sort parent channels by metric
        sortedChannels.sort((a, b) => {
          const aValue =
            typeof a.metrics?.[metricKey] === "number"
              ? a.metrics[metricKey]
              : 0;
          const bValue =
            typeof b.metrics?.[metricKey] === "number"
              ? b.metrics[metricKey]
              : 0;
          const comparison = aValue - bValue;
          return sortConfig.direction === "asc" ? comparison : -comparison;
        });

        // Also sort sub-channels within each parent by the same metric
        sortedChannels.forEach((parent) => {
          if (parent.subRows && parent.subRows.length > 0) {
            parent.subRows.sort((a, b) => {
              const aValue =
                typeof a.metrics?.[metricKey] === "number"
                  ? a.metrics[metricKey]
                  : 0;
              const bValue =
                typeof b.metrics?.[metricKey] === "number"
                  ? b.metrics[metricKey]
                  : 0;
              const comparison = aValue - bValue;
              return sortConfig.direction === "asc" ? comparison : -comparison;
            });
          }
        });
      }

      return sortedChannels;
    }

    // Flat structure when subchannels are disabled
    const sortedChannels = [...channelsWithMetrics];

    // Apply client-side sorting
    if (sortConfig.key === "custom_channel") {
      sortedChannels.sort((a, b) => {
        const comparison = a.custom_channel.localeCompare(b.custom_channel);
        return sortConfig.direction === "asc" ? comparison : -comparison;
      });
    } else if (sortConfig.key === "custom_subchannel") {
      sortedChannels.sort((a, b) => {
        const aVal = a.custom_subchannel || "";
        const bVal = b.custom_subchannel || "";
        const comparison = aVal.localeCompare(bVal);
        return sortConfig.direction === "asc" ? comparison : -comparison;
      });
    } else if (sortConfig.key.startsWith("metric_")) {
      const metricKey = sortConfig.key.replace(
        "metric_",
        "",
      ) as keyof CustomChannelMetrics;
      sortedChannels.sort((a, b) => {
        const aValue =
          typeof a.metrics?.[metricKey] === "number" ? a.metrics[metricKey] : 0;
        const bValue =
          typeof b.metrics?.[metricKey] === "number" ? b.metrics[metricKey] : 0;
        const comparison = aValue - bValue;
        return sortConfig.direction === "asc" ? comparison : -comparison;
      });
    }

    return sortedChannels;
  }, [customChannelsData, sortConfig, isGrouped, debouncedSearchQuery]);

  // Setup for CSV export
  const { exportCustomChannelsAsync } = useCustomChannelsExport();

  // Export params for channels only (without subchannels)
  const channelsOnlyParams = useMemo(() => {
    return baseQueryParams;
  }, [baseQueryParams]);

  // Export params for channels with subchannels
  const channelsWithSubchannelsParams = useMemo(
    () => ({
      ...baseQueryParams,
      extra_fields: "custom_subchannel",
    }),
    [baseQueryParams],
  );

  // Columns for channels-only export
  const channelsOnlyColumns = useMemo(
    () => getCustomChannelColumns(selectedMetrics, handleMetricClick, true),
    [selectedMetrics, handleMetricClick],
  );

  // Columns for channels with subchannels export
  const channelsWithSubchannelsColumns = useMemo(
    () => getCustomChannelColumns(selectedMetrics, handleMetricClick, false),
    [selectedMetrics, handleMetricClick],
  );

  // Export hook for channels only
  const {
    exportData: exportChannelsOnlyData,
    isExporting: isExportingChannelsOnly,
    defaultFilename: defaultFilenameChannelsOnly,
  } = usePaginatedCSVExport({
    fetchAllData: () => exportCustomChannelsAsync(channelsOnlyParams),
    columns: channelsOnlyColumns,
    filename: `custom-channels-only-${formatDateForFilename(new Date())}.csv`,
    onError: (error) => {
      console.error("CSV export failed:", error);
      toast.error("Failed to export custom channels data");
    },
  });

  // Export hook for channels with subchannels
  const {
    exportData: exportWithSubchannelsData,
    isExporting: isExportingWithSubchannels,
    defaultFilename: defaultFilenameWithSubchannels,
  } = usePaginatedCSVExport({
    fetchAllData: () =>
      exportCustomChannelsAsync(channelsWithSubchannelsParams),
    columns: channelsWithSubchannelsColumns,
    filename: `custom-channels-with-subchannels-${formatDateForFilename(
      new Date(),
    )}.csv`,
    onError: (error) => {
      console.error("CSV export failed:", error);
      toast.error("Failed to export custom channels data");
    },
  });

  // Combined exporting state
  const isExporting = isExportingChannelsOnly || isExportingWithSubchannels;

  // State for export filename dialogs
  // Separate state for main button (uses popover) vs dropdown menu items (use dialogs)
  const [isMainExportPopoverOpen, setIsMainExportPopoverOpen] = useState(false);
  const [dropdownExportDialogType, setDropdownExportDialogType] = useState<
    "channels-only" | "with-subchannels" | null
  >(null);

  const handleExportChannelsOnly = (filename: string) => {
    exportChannelsOnlyData(filename);
  };

  const handleExportWithSubchannels = (filename: string) => {
    exportWithSubchannelsData(filename);
  };

  // Column state management (with resizing)
  const {
    columnOrder,
    setColumnOrder,
    columnSizing,
    setColumnSizing,
    pinnedColumns,
    toggleColumnPin,
    wrappedColumns,
    setWrappedColumns,
    toggleColumnWrap,
    resetToDefaultPinned,
    dndState: { sensors, handleDragEnd },
  } = usePersistedColumnState("custom-channels::list::columns", {
    getDefaultPinnedColumns: () => ["custom_channel"],
  });

  // Convert to TanStack columnDefs with resizing enabled
  const columnDefs = useMemo<ColumnDef<CustomChannel>[]>(() => {
    const allDefs = getCustomChannelColumnDefs(
      selectedMetrics,
      handleMetricClick,
      isGrouped,
    );
    return allDefs.map((col) => ({ ...col, enableResizing: true }));
  }, [selectedMetrics, handleMetricClick, isGrouped]);

  // Function to handle double-click on column resize handle
  const handleDoubleClickResize = useCallback(
    createColumnResizeHandler(setColumnSizing, "custom-channels-table"),
    [],
  );

  // Single column resize handler
  const handleSingleColumnResize = useMemo(
    () =>
      createSingleColumnResizeHandler(setColumnSizing, "custom-channels-table"),
    [setColumnSizing],
  );

  // Bulk resize handler
  const handleBulkColumnResize = useMemo(
    () =>
      createBulkColumnResizeHandler(setColumnSizing, "custom-channels-table"),
    [setColumnSizing],
  );

  // Handle removing a column from view
  const handleRemoveColumn = useCallback(
    (columnId: string) => {
      const column = columns.find((col) => col.key === columnId);

      if (column?.columnType === "metric") {
        const metricKey = columnId.replace(
          "metric_",
          "",
        ) as keyof CustomChannelMetrics;
        setSelectedMetrics((prev) => prev.filter((m) => m !== metricKey));
      } else {
        setColumnVisibility((prev) => ({
          ...prev,
          [columnId]: false,
        }));
      }
    },
    [columns, setColumnVisibility, setSelectedMetrics],
  );

  // Column alias handlers
  const handleSetAlias = useCallback((columnId: string) => {
    setAliasColumnId(columnId);
    setShowAliasModal(true);
  }, []);

  const handleSaveAlias = useCallback(
    (columnId: string, alias: string) => {
      setColumnAliases((prev) => ({ ...prev, [columnId]: alias }));
    },
    [setColumnAliases],
  );

  const handleClearAlias = useCallback(
    (columnId: string) => {
      setColumnAliases((prev) => {
        const updated = { ...prev };
        delete updated[columnId];
        return updated;
      });
    },
    [setColumnAliases],
  );

  const handleResetAliases = useCallback(() => {
    setColumnAliases({});
  }, [setColumnAliases]);

  // Reference to container element
  const tableContainerRef = useRef<HTMLDivElement | null>(null);

  const columnIds = useMemo(
    () => columnDefs.map((col) => col.id!),
    [columnDefs],
  );

  // Initialize column order when columnDefs change (custom logic for custom_subchannel)
  useEffect(() => {
    setColumnOrder((prevOrder) => {
      if (prevOrder.length === 0) {
        // No saved order, use default with pinned columns first
        const pinnedFirst = columnIds.filter((id) =>
          pinnedColumns.includes(id),
        );
        const unpinned = columnIds.filter((id) => !pinnedColumns.includes(id));
        return [...pinnedFirst, ...unpinned];
      }

      const validSavedColumns = prevOrder.filter((id) =>
        columnIds.includes(id),
      );
      const newColumns = columnIds.filter((id) => !prevOrder.includes(id));

      // If custom_subchannel is a new column, insert it after custom_channel
      let newOrder: string[];
      if (
        newColumns.includes("custom_subchannel") &&
        validSavedColumns.includes("custom_channel")
      ) {
        const customChannelIndex = validSavedColumns.indexOf("custom_channel");
        const beforeCustomChannel = validSavedColumns.slice(
          0,
          customChannelIndex + 1,
        );
        const afterCustomChannel = validSavedColumns.slice(
          customChannelIndex + 1,
        );
        const otherNewColumns = newColumns.filter(
          (id) => id !== "custom_subchannel",
        );
        newOrder = [
          ...beforeCustomChannel,
          "custom_subchannel",
          ...afterCustomChannel,
          ...otherNewColumns,
        ];
      } else {
        newOrder = [...validSavedColumns, ...newColumns];
      }

      // Only update if order actually changed
      if (
        newOrder.length !== prevOrder.length ||
        newOrder.some((id, i) => prevOrder[i] !== id)
      ) {
        return newOrder;
      }

      return prevOrder;
    });
  }, [columnIds, setColumnOrder, pinnedColumns]);

  // Table configuration
  const table = useReactTable({
    data: customChannels,
    columns: columnDefs,
    state: {
      columnSizing,
      columnOrder,
      columnVisibility,
      expanded: isGrouped ? expanded : {},
    },
    onExpandedChange: isGrouped ? setExpanded : undefined,
    getSubRows: isGrouped
      ? (row) => (row as CustomChannel & { subRows?: CustomChannel[] }).subRows
      : undefined,
    getExpandedRowModel: isGrouped ? getExpandedRowModel() : undefined,
    onColumnSizingChange: setColumnSizing,
    columnResizeMode: "onChange",
    getCoreRowModel: getCoreRowModel(),
    debugTable: false,
    defaultColumn: {
      minSize: 80,
      size: 150,
    },
  });

  // Total count
  const totalCount = customChannels.length;

  // Loading state
  const isLoading = isLoadingCustomChannels;

  useEffect(() => {
    if (isLoading && selectedMetrics.length > 0) {
      const timer = setTimeout(() => {
        setShowPerformanceMessage(true);
      }, 5000);

      return () => clearTimeout(timer);
    } else {
      setShowPerformanceMessage(false);
    }
  }, [isLoading, selectedMetrics.length]);

  // Check if the metric limit is exceeded
  const isMetricLimitExceeded = totalCount > CUSTOM_CHANNEL_METRIC_LIMIT;

  // Handle sorting
  const handleSort = (colId: string) => {
    const unifiedColumn = columns.find((col) => col.key.toString() === colId);
    const sortable = unifiedColumn?.sortable;

    if (!sortable) return;

    const isMetricColumn = colId.startsWith("metric_");
    if (isMetricColumn && isMetricLimitExceeded) return;

    let direction: SortDirection = unifiedColumn?.defaultSortDirection || "asc";

    if (sortConfig.key === colId) {
      direction = sortConfig.direction === "asc" ? "desc" : "asc";
    }

    setSortConfig({ key: colId, direction });
    updateFilter("sortColumn", colId);
    updateFilter("sortDirection", direction);
  };

  // Render sort icon
  const renderSortIcon = useCallback(
    (props: { key: string; sortable?: boolean }) => {
      if (!props.sortable) return null;

      const isMetricColumn = props.key.startsWith("metric_");
      if (isMetricColumn && isMetricLimitExceeded) {
        return null;
      }

      return (
        <SortIcon
          isActive={sortConfig.key === props.key}
          direction={sortConfig.key === props.key ? sortConfig.direction : null}
          showOnHover={false}
        />
      );
    },
    [sortConfig, isMetricLimitExceeded],
  );

  // Helper function for metric limit tooltip
  const getMetricLimitTooltipText = () => {
    return `Apply additional filters to sort on a metric. For performance reasons, sorting on metrics is currently disabled when more than ${CUSTOM_CHANNEL_METRIC_LIMIT} custom channels match filters.`;
  };

  // Memoize pinnedColumnPositions calculation
  const pinnedColumnPositions = useMemo(() => {
    const positions: Record<string, number> = {};
    let cumulativeLeft = 0;

    columnOrder.forEach((colId) => {
      if (pinnedColumns.includes(colId)) {
        positions[colId] = cumulativeLeft;
        const column = table.getColumn(colId);
        if (column) {
          cumulativeLeft += column.getSize();
        }
      }
    });

    return positions;
  }, [columnOrder, pinnedColumns, table]);

  // Render loading state
  const renderLoadingState = () => {
    const headerGroups = table.getHeaderGroups();

    return (
      <>
        <TableHeader>
          {headerGroups.map((headerGroup) => (
            <TableRow key={headerGroup.id} className="bg-muted/50">
              {headerGroup.headers.map((header) => (
                <TableHead key={header.id} style={{ width: header.getSize() }}>
                  <Skeleton className="h-4 w-full" />
                </TableHead>
              ))}
            </TableRow>
          ))}
        </TableHeader>
        <TableBody className="bg-card">
          {Array.from({ length: 7 }).map((_, i) => (
            <TableRow key={i}>
              {i === 3 && showPerformanceMessage ? (
                <TableCell
                  colSpan={headerGroups[0].headers.length}
                  className="py-6"
                >
                  <div className="flex items-center">
                    <div
                      role="status"
                      aria-live="polite"
                      className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-muted/50 border border-muted-foreground/10 animate-in fade-in duration-500"
                    >
                      <svg
                        className="w-4 h-4 text-primary flex-shrink-0"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                        aria-hidden="true"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M13 10V3L4 14h7v7l9-11h-7z"
                        />
                      </svg>
                      <span className="text-sm font-medium text-muted-foreground">
                        To speed up query time, try adding metric filters or
                        reducing the number of metric columns
                      </span>
                    </div>
                  </div>
                </TableCell>
              ) : (
                headerGroups[0].headers.map((header) => (
                  <TableCell key={`${i}-${header.id}`}>
                    <Skeleton className="h-4 w-full" />
                  </TableCell>
                ))
              )}
            </TableRow>
          ))}
        </TableBody>
      </>
    );
  };

  // Generate export button tooltip
  const exportTooltipText = useMemo(() => {
    if (isMetricLimitExceeded && selectedMetrics.length > 0) {
      return `To maintain performance, exporting metrics for more than ${CUSTOM_CHANNEL_METRIC_LIMIT} custom channels is not currently supported. Please filter to less than ${CUSTOM_CHANNEL_METRIC_LIMIT} custom channels, or remove metric columns in order to export`;
    }
    return "Export custom channels data to CSV";
  }, [isMetricLimitExceeded, selectedMetrics.length]);

  // Determine if export should be disabled
  const isExportDisabled = useMemo(() => {
    return (
      isExporting ||
      isLoading ||
      customChannels.length === 0 ||
      (isMetricLimitExceeded && selectedMetrics.length > 0)
    );
  }, [
    isExporting,
    isLoading,
    customChannels.length,
    isMetricLimitExceeded,
    selectedMetrics.length,
  ]);

  // Reset ONLY column widths
  const resetColumnWidths = useCallback(() => {
    setColumnSizing({});
  }, [setColumnSizing]);

  // Reset ONLY column order
  const resetColumnOrder = useCallback(() => {
    setColumnOrder((prev) => {
      const defaultOrder = columnDefs.map((c) => c.id!);
      const pinnedFirst = prev.filter((id) => pinnedColumns.includes(id));
      const unpinnedDefault = defaultOrder.filter(
        (id) => !pinnedColumns.includes(id),
      );
      return [...pinnedFirst, ...unpinnedDefault];
    });
  }, [setColumnOrder, columnDefs, pinnedColumns]);

  // Reset pinned columns
  const resetPinnedColumns = useCallback(() => {
    resetToDefaultPinned();
  }, [resetToDefaultPinned]);

  // Reset wrapped columns
  const resetWrappedColumns = useCallback(() => {
    setWrappedColumns([]);
  }, [setWrappedColumns]);

  // Reset sort order to defaults
  const resetSortOrder = useCallback(() => {
    const defaults = getDefaultSortSettings();
    setSortConfig({
      key: defaults.sortColumn,
      direction: defaults.sortDirection,
    });
    updateFilter("sortColumn", defaults.sortColumn);
    updateFilter("sortDirection", defaults.sortDirection);
  }, [getDefaultSortSettings, updateFilter]);

  // Comprehensive table reset function
  const handleResetTable = useCallback(() => {
    handleClearAllFilters();
    resetColumnVisibility();
    clearAllMetricFilters();
    setColumnOrder([]);
    setColumnSizing({});
    resetPinnedColumns();
    setColumnAliases({});
    resetWrappedColumns();
    toast.success("Table reset to default state");
  }, [
    handleClearAllFilters,
    resetColumnVisibility,
    clearAllMetricFilters,
    setColumnOrder,
    setColumnSizing,
    resetPinnedColumns,
    setColumnAliases,
    resetWrappedColumns,
  ]);

  // Render table content
  const renderTableContent = () => {
    const rowModel = table.getRowModel();

    return (
      <>
        <DraggableTableHeader
          table={table}
          columnOrder={columnOrder}
          handleSort={handleSort}
          sortConfig={sortConfig}
          renderSortIcon={renderSortIcon}
          filters={filters}
          updateFilter={updateFilter}
          clearFilter={clearFilter}
          columns={columns}
          isMetricLimitExceeded={isMetricLimitExceeded}
          getMetricLimitTooltipText={getMetricLimitTooltipText}
          handleDoubleClickResize={handleDoubleClickResize}
          pinnedColumns={pinnedColumns}
          onTogglePin={toggleColumnPin}
          wrappedColumns={wrappedColumns}
          onToggleWrap={toggleColumnWrap}
          onResizeToFit={handleSingleColumnResize}
          onRemoveColumn={handleRemoveColumn}
          columnAliases={columnAliases}
          onSetAlias={handleSetAlias}
        />
        <TableBody className="bg-card">
          {isError ? (
            <TableRow>
              <TableCell
                colSpan={table.getAllColumns().length}
                className="h-24 text-center text-muted-foreground"
              >
                Error loading custom channels:{" "}
                {error?.message || "Unknown error"}
              </TableCell>
            </TableRow>
          ) : customChannels.length === 0 ? (
            <TableRow>
              <TableCell
                colSpan={table.getAllColumns().length}
                className="h-24 text-center text-muted-foreground"
              >
                No matching custom channels
              </TableCell>
            </TableRow>
          ) : (
            rowModel.rows.map((row) => {
              const isSubRow = row.depth > 0;
              const isParentRow = row.depth === 0;
              const isExpanded = row.getIsExpanded();

              // Determine if this parent should be sticky (only in grouped mode)
              const shouldBeSticky =
                isGrouped && enableStickyParents && isParentRow && isExpanded;

              return (
                <TableRow
                  key={row.id}
                  className={cn(
                    isSubRow && "bg-muted/20",
                    shouldBeSticky && "sticky-parent-row",
                  )}
                  style={{
                    ...(shouldBeSticky && {
                      position: "sticky",
                      top: "41px",
                      zIndex: 5,
                      backgroundColor: "hsl(var(--background))",
                      boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                    }),
                  }}
                >
                  {row.getVisibleCells().map((cell, cellIndex) => {
                    const isPinned = pinnedColumns.includes(cell.column.id);
                    const isWrapped = wrappedColumns.includes(cell.column.id);
                    const pinnedLeft = pinnedColumnPositions[cell.column.id];

                    return (
                      <TableCell
                        key={cell.id}
                        style={{
                          width: cell.column.getSize(),
                          minWidth: cell.column.getSize(),
                          maxWidth: cell.column.getSize(),
                          ...(isPinned && { left: `${pinnedLeft}px` }),
                          ...(isSubRow &&
                            cellIndex === 0 && { paddingLeft: "3rem" }),
                        }}
                        className={cn(
                          cell.column.id === sortConfig.key && "bg-muted/40",
                          isPinned && "pinned-column bg-background z-50",
                        )}
                        data-column-id={cell.column.id}
                      >
                        <div
                          className={cn("cell-content", isWrapped && "wrapped")}
                          title={String(row.getValue(cell.column.id) || "")}
                        >
                          {flexRender(
                            cell.column.columnDef.cell,
                            cell.getContext(),
                          )}
                        </div>
                      </TableCell>
                    );
                  })}
                </TableRow>
              );
            })
          )}
        </TableBody>
      </>
    );
  };

  return (
    <Card className="h-full relative flex flex-col">
      <div className="p-4 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search className="absolute left-0 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder="Filter by channel name..."
              className="w-[250px] border-0 border-b border-border shadow-none rounded-none pl-7 pr-8 h-10 placeholder:text-muted-foreground focus:ring-0 focus-visible:ring-0 focus:border-muted-foreground transition-colors bg-transparent hover:border-muted-foreground"
              onChange={(e) => handleSearchChange(e.target.value)}
              value={searchInputValue}
              aria-label="Search custom channels"
            />
            {searchInputValue && (
              <Button
                variant="ghost"
                size="icon"
                className="absolute right-0 top-1/2 -translate-y-1/2 h-6 w-6 p-0 hover:bg-transparent"
                onClick={clearSearch}
                aria-label="Clear search"
              >
                <X className="h-4 w-4 text-muted-foreground hover:text-foreground/80" />
              </Button>
            )}
          </div>
          <InfoTooltip
            trigger={
              <button
                className="ml-2 text-muted-foreground hover:text-foreground transition-colors"
                aria-label="Wildcard search info"
                onClick={(e) => e.stopPropagation()}
              >
                <InfoCircledIcon className="h-3.5 w-3.5" />
              </button>
            }
            side="right"
            sideOffset={5}
          >
            <div className="text-sm space-y-2">
              <p className="font-medium">Supports wildcards</p>
              <ul className="space-y-1">
                <li>
                  <code className="px-1 py-0.5 bg-muted rounded text-xs">
                    _
                  </code>{" "}
                  matches a single character
                </li>
                <li>
                  <code className="px-1 py-0.5 bg-muted rounded text-xs">
                    %
                  </code>{" "}
                  matches zero or more characters
                </li>
              </ul>
            </div>
          </InfoTooltip>
        </div>
        <div className="flex items-center gap-2">
          <div className="text-sm text-muted-foreground">
            {isLoading
              ? "Loading..."
              : totalCount > 0
                ? `${totalCount.toLocaleString()} matching ${
                    includeSubchannel ? "rows" : "channels"
                  }`
                : "No matching channels"}
          </div>

          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  const newValue = !isGrouped;
                  setIsGrouped(newValue);
                  if (newValue && includeSubchannel) {
                    setExpanded(true);
                  }
                }}
                className="flex items-center gap-1.5"
              >
                {isGrouped ? (
                  <>
                    <ListChevronsUpDown className="h-4 w-4" />
                    <span>Ungroup by Channel</span>
                  </>
                ) : (
                  <>
                    <ListCollapse className="h-4 w-4" />
                    <span>Group by Channel</span>
                  </>
                )}
              </Button>
            </TooltipTrigger>
            <TooltipContent>
              <p>
                {isGrouped
                  ? "Show sub-channels in a separate column"
                  : "Group sub-channels under parent channels"}
              </p>
            </TooltipContent>
          </Tooltip>

          {isGrouped && (
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => {
                    const isCurrentlyExpanded =
                      Object.keys(expanded).length > 0 || expanded === true;
                    if (isCurrentlyExpanded) {
                      setExpanded({});
                      setIncludeSubchannel(false);
                    } else {
                      setExpanded(true);
                      setIncludeSubchannel(true);
                    }
                  }}
                  className="h-8 w-8"
                >
                  {Object.keys(expanded).length > 0 || expanded === true ? (
                    <Shrink className="h-4 w-4" />
                  ) : (
                    <Expand className="h-4 w-4" />
                  )}
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                <p>
                  {Object.keys(expanded).length > 0 || expanded === true
                    ? "Collapse all sub-channels"
                    : "Expand all sub-channels"}
                </p>
              </TooltipContent>
            </Tooltip>
          )}

          <ChannelsColumnSelector
            selectedMetrics={selectedMetrics as Array<keyof ChannelMetrics>}
            onSelectMetrics={
              handleSelectMetrics as (
                metrics: Array<keyof ChannelMetrics>,
              ) => void
            }
            showMetricsSection={true}
            defaultMetrics={getDefaultMetrics() as Array<keyof ChannelMetrics>}
            metricOrder={DEFAULT_METRIC_ORDER}
            metricConfig={
              METRIC_CONFIG as Record<
                string,
                { title: string; tooltip?: string; category: string }
              >
            }
          />

          {selectedMetrics.length > 0 && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowFilterModal(true)}
              className="flex items-center gap-1.5"
            >
              <Filter
                className={`h-4 w-4 ${
                  hasAccountFilters ||
                  hasOpportunityFilters ||
                  hasTouchpointFilters
                    ? "text-primary"
                    : "text-muted-foreground"
                }`}
              />
              <span>Metric Filters</span>
              {totalMetricFilterCount > 0 && (
                <Badge className="ml-1 bg-primary text-primary-foreground">
                  {totalMetricFilterCount}
                </Badge>
              )}
            </Button>
          )}

          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="outline"
                size="icon"
                onClick={() => {
                  const visibleColumnIds = columnOrder.filter(
                    (columnId) => columnVisibility[columnId] !== false,
                  );
                  handleBulkColumnResize(visibleColumnIds);
                }}
                className="h-8 w-8"
              >
                <UnfoldHorizontal className="h-4 w-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>
              <p>Resize all visible columns to fit their content</p>
            </TooltipContent>
          </Tooltip>

          <ResetTableMenu
            onResetWidths={resetColumnWidths}
            onResetOrder={resetColumnOrder}
            onResetColumns={resetColumnVisibility}
            onResetTableFilters={handleClearAllFilters}
            onResetMetricFilters={clearAllMetricFilters}
            onResetPinnedColumns={resetPinnedColumns}
            onResetSort={resetSortOrder}
            onResetAliases={handleResetAliases}
            onResetWrappedColumns={resetWrappedColumns}
            onResetAll={handleResetTable}
          />

          <div className="inline-flex">
            <ExportFilenameDialog
              open={isMainExportPopoverOpen}
              onOpenChange={setIsMainExportPopoverOpen}
              defaultFilename={defaultFilenameWithSubchannels}
              extension="csv"
              onExport={handleExportWithSubchannels}
              disabled={isExportDisabled}
            >
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setIsMainExportPopoverOpen(true)}
                    disabled={isExportDisabled}
                    className="rounded-r-none flex items-center gap-1.5"
                  >
                    {isExporting ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Download className="h-4 w-4" />
                    )}
                  </Button>
                </TooltipTrigger>
                <TooltipContent className="max-w-[20rem]">
                  <p>{exportTooltipText}</p>
                </TooltipContent>
              </Tooltip>
            </ExportFilenameDialog>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={isExportDisabled}
                  className="rounded-l-none border-l-0 px-2"
                >
                  <ChevronDown className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem
                  onClick={() => setDropdownExportDialogType("channels-only")}
                >
                  Export Channels Only
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() =>
                    setDropdownExportDialogType("with-subchannels")
                  }
                >
                  Export With Sub-Channels
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </div>

      <EntityFilterModal
        open={showFilterModal}
        onOpenChange={setShowFilterModal}
        scope="custom-channels::list"
        entity="channel"
      />

      <div className="px-4 pb-2 space-y-2 min-h-8 flex-shrink-0">
        {hasFilters && (
          <div className="flex items-center justify-between">
            <ActiveFiltersDisplay
              filters={Object.fromEntries(
                Object.entries(filters).filter(
                  ([key]) => key !== "sortColumn" && key !== "sortDirection",
                ),
              )}
              columns={columns}
              clearFilter={clearFilter}
              clearAllFilters={handleClearAllFilters}
              clearAllText="Clear all table filters"
              filterType="table"
            />
          </div>
        )}

        {Object.keys(metricDisplayFilters).length > 0 && (
          <div className="flex items-center justify-between">
            <ActiveFiltersDisplay
              filters={metricDisplayFilters}
              columns={metricFilterColumns}
              clearFilter={handleClearMetricFilter}
              clearAllFilters={clearAllMetricFilters}
              clearAllText="Clear all metric filters"
              filterType="metric"
            />
          </div>
        )}
      </div>

      <DndContext
        sensors={sensors}
        collisionDetection={constrainedClosestCenter}
        onDragEnd={handleDragEnd}
        modifiers={[restrictToTableBounds]}
      >
        <div
          ref={tableContainerRef}
          className={cn(
            "rounded-md border-t border-x overflow-y-auto flex-grow",
            table.getState().columnSizingInfo.isResizingColumn &&
              "table-resizing",
          )}
          style={{
            maxWidth: isMobile
              ? "calc(100vw - 2rem)"
              : state === "collapsed"
                ? "calc(100vw - var(--sidebar-width-icon) - 2rem)"
                : "calc(100vw - var(--sidebar-width) - 2rem)",
            minWidth: "640px",
          }}
        >
          <Table
            className="w-full table-fixed"
            style={{ width: table.getTotalSize() }}
            role="table"
            aria-label="Custom channels data table"
            aria-describedby="custom-channels-list-heading"
            data-table-id="custom-channels-table"
            data-table-container
          >
            {isLoading ? renderLoadingState() : renderTableContent()}
          </Table>
        </div>
      </DndContext>

      <ColumnAliasModal
        open={showAliasModal}
        onOpenChange={setShowAliasModal}
        columnId={aliasColumnId}
        originalTitle={(() => {
          const column = columns.find((col) => col.key === aliasColumnId);
          return typeof column?.title === "string"
            ? column.title
            : aliasColumnId;
        })()}
        originalDescription={(() => {
          if (aliasColumnId.startsWith("metric_")) {
            const metricKey = aliasColumnId.replace("metric_", "");
            return METRIC_CONFIG[metricKey as keyof typeof METRIC_CONFIG]
              ?.tooltip;
          }
          return undefined;
        })()}
        currentAlias={columnAliases[aliasColumnId]}
        onSave={handleSaveAlias}
        onClear={handleClearAlias}
      />

      {drilldownState && (
        <MetricDrilldown
          open={!!drilldownState}
          onOpenChange={(open) => {
            if (!open) setDrilldownState(null);
          }}
          channel={drilldownState.channel}
          subchannel={drilldownState.subchannel}
          metric={drilldownState.metric}
          metricValue={drilldownState.metricValue}
          filters={{
            account_filters: accountFilters,
            opportunity_filters: opportunityFilters,
            touchpoint_filters: touchpointFilters,
          }}
          metrics={drilldownChannelMetrics}
          loadingStates={
            drilldownLoadingStates as Record<
              keyof CustomChannelMetrics,
              { isLoading: boolean; isError: boolean }
            >
          }
          drillDownLimit={DRILLDOWN_LIMIT}
          rule_set_id={selectedRulesetId ?? undefined}
        />
      )}

      {/* Dialogs for dropdown export options (no children = uses Dialog) */}
      <ExportFilenameDialog
        open={dropdownExportDialogType === "channels-only"}
        onOpenChange={(open) => !open && setDropdownExportDialogType(null)}
        defaultFilename={defaultFilenameChannelsOnly}
        extension="csv"
        onExport={handleExportChannelsOnly}
        disabled={isExportDisabled}
      />

      <ExportFilenameDialog
        open={dropdownExportDialogType === "with-subchannels"}
        onOpenChange={(open) => !open && setDropdownExportDialogType(null)}
        defaultFilename={defaultFilenameWithSubchannels}
        extension="csv"
        onExport={handleExportWithSubchannels}
        disabled={isExportDisabled}
      />
    </Card>
  );
}
